// Iter 83.5 / Phase E4 Sprint 5 — Outstanding Recoveries Console.
// Built on D23 negative-FNF records. Operators can notify ex-employees,
// record partial repayments, mark disputed, escalate to legal, or
// write off (super-admin only). Every action is audit-logged.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Eye, Phone, EnvelopeSimple, ArrowsClockwise, MagnifyingGlass,
    CurrencyInr, ShieldWarning, Gavel, CheckCircle, XCircle,
    ClockClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

const STATUS_TONE = {
    Pending: "bg-slate-100 text-slate-700",
    Notified: "bg-blue-100 text-blue-800",
    PartiallyRecovered: "bg-amber-100 text-amber-800",
    Recovered: "bg-emerald-100 text-emerald-800",
    Disputed: "bg-orange-100 text-orange-800",
    LegalEscalated: "bg-rose-100 text-rose-800",
    WrittenOff: "bg-slate-200 text-slate-600 line-through",
};

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OutstandingRecoveriesConsole() {
    const { can } = useAuth();
    const canWrite = can("recovery:admin");
    const canWriteOff = can("recovery:writeoff");
    const [meta, setMeta] = useState(null);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [dialog, setDialog] = useState({ kind: "", open: false });
    const [form, setForm] = useState({});

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status_filter", statusFilter);
            if (search.trim()) params.set("q", search.trim());
            const [list, sum] = await Promise.all([
                api.get(`/recovery/list?${params.toString()}`),
                api.get(`/recovery/dashboard/summary`),
            ]);
            setRows(list.data.items || []);
            setSummary(sum.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load recoveries"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search]);

    useEffect(() => { reload(); }, [reload]);
    useEffect(() => {
        (async () => {
            try {
                const m = await api.get(`/recovery/meta`);
                setMeta(m.data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load recovery meta"));
            }
        })();
    }, []);

    const openDetail = async (rec) => {
        try {
            const { data } = await api.get(`/recovery/${rec.id}`);
            setDetail(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load recovery details"));
        }
    };

    const openDialog = (kind, defaultForm = {}) => {
        setForm(defaultForm);
        setDialog({ kind, open: true });
    };

    const closeDialog = () => {
        setDialog({ kind: "", open: false });
        setForm({});
    };

    const submitDialog = async () => {
        if (!detail) return;
        const recId = detail.recovery.id;
        try {
            let r;
            if (dialog.kind === "notify") {
                r = await api.post(`/recovery/${recId}/notify`, {
                    method: form.method || "Email",
                    note: form.note || "",
                });
            } else if (dialog.kind === "payment") {
                r = await api.post(`/recovery/${recId}/payment`, {
                    amount: parseFloat(form.amount || "0"),
                    mode: form.mode || "Bank Transfer",
                    reference: form.reference || "",
                    received_on: form.received_on || null,
                    note: form.note || "",
                });
            } else if (dialog.kind === "dispute") {
                r = await api.post(`/recovery/${recId}/dispute`, { note: form.note || "" });
            } else if (dialog.kind === "escalate") {
                r = await api.post(`/recovery/${recId}/escalate`, {
                    note: form.note || "",
                    legal_ref: form.legal_ref || "",
                });
            } else if (dialog.kind === "writeoff") {
                r = await api.post(`/recovery/${recId}/writeoff`, {
                    note: form.note || "",
                    reason_code: form.reason_code || "OperatorWriteOff",
                });
            }
            toast.success(`Action completed · status: ${r.data.status}`);
            closeDialog();
            // refresh detail + list
            const { data: fresh } = await api.get(`/recovery/${recId}`);
            setDetail(fresh);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Action failed"));
        }
    };

    const renderSummary = () => {
        if (!summary) return null;
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recovery-summary">
                <Card title="Open records" value={summary.open_count || 0} tone="bg-amber-50" />
                <Card title="Open shortfall" value={fmtMoney(summary.open_shortfall_total)} tone="bg-rose-50" />
                <Card title="Closed records" value={summary.closed_count || 0} tone="bg-emerald-50" />
                <Card title="Total records" value={summary.total_records || 0} tone="bg-slate-50" />
            </div>
        );
    };

    return (
        <div className="space-y-4" data-testid="outstanding-recoveries-console">
            <PageHeader
                title="Outstanding Recoveries (D23)"
                subtitle="Negative-FNF receivables lifecycle. Notify · record repayments · dispute · escalate · write off. Every action is append-only audit-logged."
                actions={
                    <Button variant="outline" size="sm" onClick={reload} data-testid="recovery-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            {renderSummary()}

            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-8 border rounded px-2 text-sm"
                    data-testid="recovery-status-filter"
                >
                    <option value="">All statuses</option>
                    {(meta?.statuses || []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                <div className="relative max-w-xs">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by id / employee / FNF…"
                        className="pl-8 h-8 text-sm"
                        data-testid="recovery-search"
                    />
                </div>
                <div className="ml-auto text-xs text-slate-500">
                    Showing {rows.length} record{rows.length === 1 ? "" : "s"}
                </div>
            </div>

            <div className="border rounded">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Recovery ID</th>
                            <th className="px-3 py-2 text-left">Employee · FNF</th>
                            <th className="px-3 py-2 text-right">Shortfall</th>
                            <th className="px-3 py-2 text-right">Paid</th>
                            <th className="px-3 py-2 text-right">Balance</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Last updated</th>
                            <th className="px-3 py-2 text-right">Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-6 text-slate-400">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-6 text-slate-400">No outstanding recoveries</td></tr>
                        ) : (
                            rows.map((r) => (
                                <tr key={r.id} className="border-b hover:bg-slate-50/50" data-testid={`recovery-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-xs">{r.employee_name_snapshot || r.employee_id}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{r.fnf_id}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(r.shortfall_amount)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(r.paid_total)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{fmtMoney(r.balance)}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${STATUS_TONE[r.status] || ""}`}>{r.status}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-[11px]">{(r.updated_at || r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => openDetail(r)}
                                            data-testid={`recovery-detail-btn-${r.id}`}
                                        >
                                            <Eye size={12} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Recovery · {detail?.recovery?.id}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Full lifecycle for this negative-FNF record. All transitions are audit-logged.
                        </DialogDescription>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-4 text-sm">
                            {/* Employee + FNF + Exit context */}
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs border-b pb-3">
                                <Pair label="Employee" value={detail.employee?.full_name || detail.recovery.employee_id} />
                                <Pair label="Employee code" value={detail.employee?.employee_code} />
                                <Pair label="FNF" value={detail.recovery.fnf_id} mono />
                                <Pair label="Exit Type · LWD" value={detail.fnf ? `${detail.fnf.exit_type} · ${detail.fnf.lwd}` : "—"} />
                                <Pair label="Approved By" value={detail.recovery.approver_email} />
                                <Pair label="Created" value={(detail.recovery.created_at || "").slice(0, 19).replace("T", " ")} />
                            </div>

                            {/* Money */}
                            <div className="grid grid-cols-3 gap-3">
                                <Card title="Shortfall" value={fmtMoney(detail.recovery.shortfall_amount)} tone="bg-rose-50" />
                                <Card title="Paid" value={fmtMoney(detail.recovery.paid_total)} tone="bg-emerald-50" />
                                <Card title="Balance" value={fmtMoney(detail.recovery.balance)} tone="bg-amber-50" />
                            </div>

                            {/* Status + actions */}
                            <div className="flex items-center gap-2">
                                <Badge className={STATUS_TONE[detail.recovery.status]}>{detail.recovery.status}</Badge>
                                <div className="text-[11px] text-slate-500">
                                    last update {(detail.recovery.updated_at || detail.recovery.created_at || "").slice(0, 19).replace("T", " ")}
                                </div>
                            </div>

                            {canWrite && !["Recovered", "WrittenOff"].includes(detail.recovery.status) && (
                                <div className="flex flex-wrap gap-2 border-t pt-2">
                                    <Button size="sm" variant="outline" onClick={() => openDialog("notify", { method: "Email" })} data-testid="recovery-notify-btn">
                                        <EnvelopeSimple size={12} className="mr-1" /> Notify
                                    </Button>
                                    <Button size="sm" onClick={() => openDialog("payment", { mode: "Bank Transfer" })} data-testid="recovery-payment-btn">
                                        <CurrencyInr size={12} className="mr-1" /> Record Payment
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => openDialog("dispute")} data-testid="recovery-dispute-btn">
                                        <ShieldWarning size={12} className="mr-1" /> Mark Disputed
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => openDialog("escalate")} data-testid="recovery-escalate-btn">
                                        <Gavel size={12} className="mr-1" /> Escalate to Legal
                                    </Button>
                                    {canWriteOff && (
                                        <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" onClick={() => openDialog("writeoff", { reason_code: "OperatorWriteOff" })} data-testid="recovery-writeoff-btn">
                                            <XCircle size={12} className="mr-1" /> Write Off
                                        </Button>
                                    )}
                                </div>
                            )}

                            {/* Payments */}
                            {(detail.recovery.payments || []).length > 0 && (
                                <div>
                                    <div className="font-semibold text-xs uppercase tracking-wide text-slate-600 mb-1">Payments ({detail.recovery.payments.length})</div>
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="text-left px-2 py-1">Date</th>
                                                <th className="text-left px-2 py-1">Mode · Ref</th>
                                                <th className="text-right px-2 py-1">Amount</th>
                                                <th className="text-left px-2 py-1">Recorded By</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.recovery.payments.map((p) => (
                                                <tr key={p.id} className="border-b">
                                                    <td className="px-2 py-1">{(p.received_on || "").slice(0, 10)}</td>
                                                    <td className="px-2 py-1">{p.mode} · <span className="font-mono">{p.reference || "—"}</span></td>
                                                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(p.amount)}</td>
                                                    <td className="px-2 py-1 text-[10px] text-slate-500">{p.recorded_by_email}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Notifications */}
                            {(detail.recovery.notifications || []).length > 0 && (
                                <div>
                                    <div className="font-semibold text-xs uppercase tracking-wide text-slate-600 mb-1">Notifications ({detail.recovery.notifications.length})</div>
                                    <div className="space-y-1">
                                        {detail.recovery.notifications.map((n) => (
                                            <div key={n.id} className="text-xs border-l-2 border-blue-300 pl-2">
                                                <span className="font-semibold">{n.method}</span> · {(n.notified_on || "").slice(0, 19).replace("T", " ")}
                                                <div className="text-slate-600 italic">&ldquo;{n.note}&rdquo;</div>
                                                <div className="text-[10px] text-slate-500">by {n.by_email}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Audit trail */}
                            <div>
                                <div className="font-semibold text-xs uppercase tracking-wide text-slate-600 mb-1">Audit Trail ({(detail.audit || []).length})</div>
                                <div className="space-y-1">
                                    {(detail.audit || []).map((a) => (
                                        <div key={a.id} className="text-xs border-b pb-1">
                                            <span className="font-semibold">{a.action}</span> · {(a.at || "").slice(0, 19).replace("T", " ")} · <span className="text-slate-500">{a.actor_email}</span>
                                            {a.payload && Object.keys(a.payload).length > 0 && (
                                                <div className="text-[10px] text-slate-500 font-mono">{JSON.stringify(a.payload)}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Action dialog */}
            <Dialog open={dialog.open} onOpenChange={(open) => { if (!open) closeDialog(); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="capitalize">{dialog.kind === "writeoff" ? "Write Off Recovery" : dialog.kind}</DialogTitle>
                        <DialogDescription className="text-xs">
                            {dialog.kind === "notify" && "Record that you've contacted the ex-employee about the outstanding amount."}
                            {dialog.kind === "payment" && "Record a repayment received. Status auto-transitions to PartiallyRecovered or Recovered."}
                            {dialog.kind === "dispute" && "Mark this recovery as disputed by the ex-employee."}
                            {dialog.kind === "escalate" && "Escalate to legal team. Future repayments still allowed."}
                            {dialog.kind === "writeoff" && "TERMINAL action — forgive the remaining balance. Requires recovery:writeoff permission."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 text-sm">
                        {dialog.kind === "notify" && (
                            <>
                                <Label>Method</Label>
                                <select
                                    value={form.method || "Email"}
                                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                                    className="w-full h-8 border rounded px-2 text-sm"
                                    data-testid="recovery-notify-method"
                                >
                                    {(meta?.notify_methods || []).map((m) => (<option key={m} value={m}>{m}</option>))}
                                </select>
                                <Label>Note</Label>
                                <Textarea
                                    value={form.note || ""}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    placeholder="e.g. Sent reminder email to ex@example.com on 2026-06-20"
                                    data-testid="recovery-notify-note"
                                />
                            </>
                        )}
                        {dialog.kind === "payment" && (
                            <>
                                <Label>Amount (₹)</Label>
                                <Input
                                    type="number" min="0.01" step="0.01"
                                    value={form.amount || ""}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    data-testid="recovery-payment-amount"
                                />
                                <Label>Mode</Label>
                                <select
                                    value={form.mode || "Bank Transfer"}
                                    onChange={(e) => setForm({ ...form, mode: e.target.value })}
                                    className="w-full h-8 border rounded px-2 text-sm"
                                    data-testid="recovery-payment-mode"
                                >
                                    {(meta?.payment_modes || []).map((m) => (<option key={m} value={m}>{m}</option>))}
                                </select>
                                <Label>Reference (UTR / Cheque #)</Label>
                                <Input
                                    value={form.reference || ""}
                                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                    data-testid="recovery-payment-ref"
                                />
                                <Label>Received On</Label>
                                <Input
                                    type="date"
                                    value={form.received_on || ""}
                                    onChange={(e) => setForm({ ...form, received_on: e.target.value })}
                                />
                                <Label>Note</Label>
                                <Input
                                    value={form.note || ""}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                />
                            </>
                        )}
                        {dialog.kind === "dispute" && (
                            <>
                                <Label>Dispute Note</Label>
                                <Textarea
                                    value={form.note || ""}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    placeholder="Reason for dispute (mandatory)"
                                    data-testid="recovery-dispute-note"
                                />
                            </>
                        )}
                        {dialog.kind === "escalate" && (
                            <>
                                <Label>Legal Ref (case # / reference)</Label>
                                <Input
                                    value={form.legal_ref || ""}
                                    onChange={(e) => setForm({ ...form, legal_ref: e.target.value })}
                                />
                                <Label>Note</Label>
                                <Textarea
                                    value={form.note || ""}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    data-testid="recovery-escalate-note"
                                />
                            </>
                        )}
                        {dialog.kind === "writeoff" && (
                            <>
                                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                                    This is a TERMINAL action. The remaining balance will be forgiven.
                                </div>
                                <Label>Reason Code</Label>
                                <Input
                                    value={form.reason_code || "OperatorWriteOff"}
                                    onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
                                />
                                <Label>Justification (min 10 chars)</Label>
                                <Textarea
                                    value={form.note || ""}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    placeholder="e.g. Compassionate write-off — employee deceased on 2026-06-15 (death certificate attached)."
                                    data-testid="recovery-writeoff-note"
                                />
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}>Cancel</Button>
                        <Button onClick={submitDialog} data-testid="recovery-dialog-submit">
                            Submit
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Card({ title, value, tone }) {
    return (
        <div className={`rounded border p-3 ${tone || "bg-slate-50"}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
            <div className="font-bold text-base mt-0.5">{value}</div>
        </div>
    );
}

function Pair({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={mono ? "font-mono text-xs" : ""}>{value || "—"}</div>
        </div>
    );
}
